/**
 * MusicPlayer 歌词高亮与滚动（抽离自 MusicPlayer.js）
 * 返回 applyLyricHighlight, clearLyricHighlight, updateMiniLyric
 */
import { useRef, useCallback } from 'react';

function addClasses(el, cls) {
  if (!el || !cls) return;
  String(cls).split(/\s+/).filter(Boolean).forEach(c => el.classList.add(c));
}
function removeClasses(el, cls) {
  if (!el || !cls) return;
  String(cls).split(/\s+/).filter(Boolean).forEach(c => el.classList.remove(c));
}

function applyLyricAnimBase(el) {
  if (!el) return;
  el.style.willChange = 'opacity, transform, box-shadow, filter';
  el.style.transition = 'opacity 260ms cubic-bezier(0.22, 1, 0.36, 1), transform 260ms cubic-bezier(0.22, 1, 0.36, 1), box-shadow 260ms ease, filter 260ms ease';
}

function isCjkLike(s) {
  try {
    const text = String(s || '');
    return /[\u3040-\u30ff\u3400-\u9fff\uac00-\ud7af]/.test(text);
  } catch (_) {
    return false;
  }
}

function buildOuterStrokeShadow(strokePx = 2) {
  const c = 'rgba(0,0,0,0.95)';
  const p = strokePx;
  const outline = [
    `${-p}px ${-p}px 0 ${c}`,
    `0 ${-p}px 0 ${c}`,
    `${p}px ${-p}px 0 ${c}`,
    `${-p}px 0 0 ${c}`,
    `${p}px 0 0 ${c}`,
    `${-p}px ${p}px 0 ${c}`,
    `0 ${p}px 0 ${c}`,
    `${p}px ${p}px 0 ${c}`
  ].join(', ');
  const drop = '0 3px 10px rgba(0,0,0,0.85)';
  return `${outline}, ${drop}`;
}

/**
 * @param {{ refsRef: React.MutableRefObject<object> }} refsRef.current 含 lyricsContainerRef, lyricLineRefs, appliedLyricHighlightRef, currentLyricLineRef, lyricSettingsRef, lyricsRef, miniLyricTextRef, miniLyricTransTextRef, highlightThemeRef, highlightGlowAlphaRef, showLyricsRef, isVideoFullscreenRef, isMobileViewportRef, videoSubtitleScaleRef
 * @param {boolean} shouldShowVideoSubtitles
 */
export function useLyricsSync({ refsRef, shouldShowVideoSubtitles }) {
  const lyricFadeOutTimerRef = useRef(null);
  const lyricScrollTimerRef = useRef(null);

  const scrollLyricToCenter = useCallback((lineEl) => {
    const refs = refsRef.current;
    const container = refs?.lyricsContainerRef?.current;
    if (!container || !lineEl) return;
    const containerRect = container.getBoundingClientRect();
    const lineRect = lineEl.getBoundingClientRect();
    const lineTopInContainer = (lineRect.top - containerRect.top) + container.scrollTop;
    const containerHeight = container.clientHeight;
    const lineHeight = lineEl.clientHeight;
    const targetScrollTop = lineTopInContainer - (containerHeight / 2) + (lineHeight / 2);
    const maxScrollTop = container.scrollHeight - containerHeight;
    const finalScrollTop = Math.max(0, Math.min(targetScrollTop, maxScrollTop));
    container.scrollTo({ top: finalScrollTop, behavior: 'smooth' });
  }, [refsRef]);

  const clearLyricHighlight = useCallback(() => {
    const refs = refsRef.current;
    if (lyricScrollTimerRef.current) {
      clearTimeout(lyricScrollTimerRef.current);
      lyricScrollTimerRef.current = null;
    }
    const prevIdx = refs?.appliedLyricHighlightRef?.current?.index ?? -1;
    if (prevIdx >= 0) {
      const prevEl = refs?.lyricLineRefs?.current?.[prevIdx];
      const prevTheme = refs?.appliedLyricHighlightRef?.current?.theme;
      if (prevEl) {
        if (prevTheme) {
          removeClasses(prevEl, prevTheme.text);
          removeClasses(prevEl, prevTheme.bg);
          removeClasses(prevEl, prevTheme.ring);
        }
        prevEl.classList.remove('font-semibold');
        prevEl.style.boxShadow = 'none';
        prevEl.style.opacity = '1';
        prevEl.style.transform = '';
        prevEl.style.filter = '';
        prevEl.style.fontSize = `${refs?.lyricSettingsRef?.current?.fontSize ?? 16}px`;
        const t = prevEl.querySelector('[data-lyric-trans]');
        if (t) {
          if (prevTheme) removeClasses(t, prevTheme.miniTrans);
          addClasses(t, 'text-gray-500');
        }
      }
    }
    if (refs?.appliedLyricHighlightRef) refs.appliedLyricHighlightRef.current = { index: -1, theme: null };
    if (refs?.currentLyricLineRef) refs.currentLyricLineRef.current = -1;
  }, [refsRef]);

  const updateMiniLyric = useCallback((idx) => {
    const refs = refsRef.current;
    if (!refs?.miniLyricTextRef?.current) return;
    const lines = refs?.lyricsRef?.current || [];
    const has = lines.length > 0;
    const safeIdx = has ? (idx >= 0 ? idx : 0) : -1;
    const line = safeIdx >= 0 ? lines[safeIdx] : null;
    const theme = refs?.highlightThemeRef?.current;
    const inVideoSubtitleMode = !!shouldShowVideoSubtitles;

    // 迷你字幕：切换时做更明显的淡入淡出
    refs.miniLyricTextRef.current.style.willChange = 'opacity, transform, filter, text-shadow';
    refs.miniLyricTextRef.current.style.transition = 'opacity 240ms cubic-bezier(0.22, 1, 0.36, 1), transform 240ms cubic-bezier(0.22, 1, 0.36, 1), filter 240ms ease';
    refs.miniLyricTextRef.current.style.opacity = '0';
    refs.miniLyricTextRef.current.style.transform = 'translateY(6px) scale(0.985)';
    refs.miniLyricTextRef.current.style.filter = 'blur(0.6px)';
    if (refs?.miniLyricTransTextRef?.current) {
      refs.miniLyricTransTextRef.current.style.willChange = 'opacity, transform, filter';
      refs.miniLyricTransTextRef.current.style.transition = 'opacity 240ms cubic-bezier(0.22, 1, 0.36, 1), transform 240ms cubic-bezier(0.22, 1, 0.36, 1), filter 240ms ease';
      refs.miniLyricTransTextRef.current.style.opacity = '0';
      refs.miniLyricTransTextRef.current.style.transform = 'translateY(6px) scale(0.985)';
      refs.miniLyricTransTextRef.current.style.filter = 'blur(0.6px)';
    }

    refs.miniLyricTextRef.current.textContent = line?.text || '';
    refs.miniLyricTextRef.current.classList.remove('text-gray-300/70');
    removeClasses(refs.miniLyricTextRef.current, theme?.miniText);
    if (inVideoSubtitleMode) {
      addClasses(refs.miniLyricTextRef.current, 'text-white');
      refs.miniLyricTextRef.current.style.letterSpacing = isCjkLike(line?.text) ? '0.12em' : '0.02em';
      const fs = Number(refs?.lyricSettingsRef?.current?.fontSize ?? 16);
      const safeFs = Number.isFinite(fs) ? fs : 16;
      const scale = (!refs?.isMobileViewportRef?.current && refs?.isVideoFullscreenRef?.current) ? (refs?.videoSubtitleScaleRef?.current || 1) : 1;
      refs.miniLyricTextRef.current.style.fontSize = `${Math.round(safeFs * scale)}px`;
    } else {
      if (idx >= 0) {
        addClasses(refs.miniLyricTextRef.current, theme?.miniText);
      } else {
        addClasses(refs.miniLyricTextRef.current, 'text-gray-300/70');
      }
      refs.miniLyricTextRef.current.style.letterSpacing = '';
      refs.miniLyricTextRef.current.style.fontSize = '';
    }

    const glow = refs?.highlightGlowAlphaRef?.current ?? 0.28;
    const baseStroke = buildOuterStrokeShadow(2);
    const glowPart = (idx >= 0 && theme?.glowRGB)
      ? `, 0 0 14px rgba(${theme.glowRGB}, ${Math.max(0.12, glow - 0.1)})`
      : '';
    refs.miniLyricTextRef.current.style.textShadow = `${baseStroke}${glowPart}`;
    try { refs.miniLyricTextRef.current.style.webkitTextStroke = '0px transparent'; } catch (_) {}
    try { refs.miniLyricTextRef.current.style.WebkitTextStroke = '0px transparent'; } catch (_) {}

    if (refs?.miniLyricTransTextRef?.current) {
      const showTrans = !!refs?.lyricSettingsRef?.current?.showTranslation;
      if (showTrans && line?.translation) {
        refs.miniLyricTransTextRef.current.style.display = '';
        refs.miniLyricTransTextRef.current.textContent = line.translation;
        removeClasses(refs.miniLyricTransTextRef.current, theme?.miniTrans);
        if (inVideoSubtitleMode) {
          addClasses(refs.miniLyricTransTextRef.current, 'text-white');
          const fs = Number(refs?.lyricSettingsRef?.current?.fontSize ?? 16);
          const safeFs = Number.isFinite(fs) ? fs : 16;
          const scale = (!refs?.isMobileViewportRef?.current && refs?.isVideoFullscreenRef?.current) ? (refs?.videoSubtitleScaleRef?.current || 1) : 1;
          const transFs = Math.max(10, Math.round(safeFs * 0.75 * scale));
          refs.miniLyricTransTextRef.current.style.fontSize = `${transFs}px`;
        } else {
          addClasses(refs.miniLyricTransTextRef.current, theme?.miniTrans);
          refs.miniLyricTransTextRef.current.style.letterSpacing = '';
          refs.miniLyricTransTextRef.current.style.fontSize = '';
        }
        const transStroke = buildOuterStrokeShadow(2);
        const transGlowPart = (idx >= 0 && theme?.glowRGB)
          ? `, 0 0 10px rgba(${theme.glowRGB}, ${Math.max(0.10, glow - 0.14)})`
          : '';
        refs.miniLyricTransTextRef.current.style.textShadow = `${transStroke}${transGlowPart}`;
        try { refs.miniLyricTransTextRef.current.style.webkitTextStroke = '0px transparent'; } catch (_) {}
        try { refs.miniLyricTransTextRef.current.style.WebkitTextStroke = '0px transparent'; } catch (_) {}
      } else {
        refs.miniLyricTransTextRef.current.style.display = 'none';
        refs.miniLyricTransTextRef.current.textContent = '';
      }
    }

    requestAnimationFrame(() => {
      if (refs?.miniLyricTextRef?.current) {
        refs.miniLyricTextRef.current.style.opacity = '1';
        refs.miniLyricTextRef.current.style.transform = 'translateY(0px) scale(1)';
        refs.miniLyricTextRef.current.style.filter = 'blur(0px)';
      }
      if (refs?.miniLyricTransTextRef?.current && refs.miniLyricTransTextRef.current.style.display !== 'none') {
        refs.miniLyricTransTextRef.current.style.opacity = '1';
        refs.miniLyricTransTextRef.current.style.transform = 'translateY(0px) scale(1)';
        refs.miniLyricTransTextRef.current.style.filter = 'blur(0px)';
      }
    });
  }, [refsRef, shouldShowVideoSubtitles]);

  const applyLyricHighlight = useCallback((nextIdx, options = {}) => {
    const refs = refsRef.current;
    const { force = false, ensureCenter = false } = options || {};
    const prevIdx = refs?.appliedLyricHighlightRef?.current?.index ?? -1;
    if (!force && nextIdx === prevIdx) {
      if (ensureCenter && refs?.showLyricsRef?.current && nextIdx >= 0) {
        const el = refs?.lyricLineRefs?.current?.[nextIdx];
        if (el) requestAnimationFrame(() => scrollLyricToCenter(el));
      }
      return;
    }

    if (prevIdx >= 0) {
      const prevEl = refs?.lyricLineRefs?.current?.[prevIdx];
      const prevTheme = refs?.appliedLyricHighlightRef?.current?.theme;
      if (prevEl) {
        applyLyricAnimBase(prevEl);
        prevEl.style.opacity = '0';
        prevEl.style.transform = 'translateY(-6px) scale(0.985)';
        prevEl.style.filter = 'blur(0.6px)';
        if (lyricFadeOutTimerRef.current) {
          clearTimeout(lyricFadeOutTimerRef.current);
          lyricFadeOutTimerRef.current = null;
        }
        lyricFadeOutTimerRef.current = setTimeout(() => {
          const r = refsRef.current;
          if (r?.currentLyricLineRef?.current !== prevIdx && prevEl) {
            if (prevTheme) {
              removeClasses(prevEl, prevTheme.text);
              removeClasses(prevEl, prevTheme.bg);
              removeClasses(prevEl, prevTheme.ring);
            }
            prevEl.classList.remove('font-semibold');
            prevEl.style.boxShadow = 'none';
            prevEl.style.fontSize = `${r?.lyricSettingsRef?.current?.fontSize ?? 16}px`;
            const t = prevEl.querySelector('[data-lyric-trans]');
            if (t) {
              if (prevTheme) removeClasses(t, prevTheme.miniTrans);
              addClasses(t, 'text-gray-500');
            }
            prevEl.style.opacity = '1';
            prevEl.style.transform = '';
            prevEl.style.filter = '';
          }
        }, 220);
      }
    }

    const nextEl = nextIdx >= 0 ? refs?.lyricLineRefs?.current?.[nextIdx] : null;
    const theme = refs?.highlightThemeRef?.current;
    if (nextEl && theme) {
      applyLyricAnimBase(nextEl);
      nextEl.style.opacity = '0';
      nextEl.style.transform = 'translateY(8px) scale(0.98)';
      nextEl.style.filter = 'blur(0.6px)';
      addClasses(nextEl, theme.text);
      addClasses(nextEl, theme.bg);
      addClasses(nextEl, theme.ring);
      nextEl.classList.add('font-semibold');
      const glow = refs?.highlightGlowAlphaRef?.current ?? 0.28;
      nextEl.style.boxShadow = `0 0 0 rgba(${theme.glowRGB}, ${Math.max(0.12, glow - 0.16)})`;
      nextEl.style.fontSize = `${(refs?.lyricSettingsRef?.current?.fontSize ?? 16) + 2}px`;
      const t = nextEl.querySelector('[data-lyric-trans]');
      if (t) {
        t.classList.remove('text-gray-500');
        addClasses(t, theme.miniTrans);
      }
      requestAnimationFrame(() => {
        if (nextEl) {
          nextEl.style.opacity = '1';
          nextEl.style.transform = 'translateY(0px) scale(1)';
          nextEl.style.filter = 'blur(0px)';
          nextEl.style.boxShadow = `0 0 26px rgba(${theme.glowRGB}, ${glow})`;
        }
      });
    }

    if (refs?.appliedLyricHighlightRef) refs.appliedLyricHighlightRef.current = { index: nextIdx, theme };
    if (refs?.currentLyricLineRef) refs.currentLyricLineRef.current = nextIdx;

    updateMiniLyric(nextIdx);

    if ((ensureCenter || refs?.showLyricsRef?.current) && refs?.showLyricsRef?.current) {
      if (lyricScrollTimerRef.current) {
        clearTimeout(lyricScrollTimerRef.current);
        lyricScrollTimerRef.current = null;
      }
      const targetIdx = nextIdx;
      lyricScrollTimerRef.current = setTimeout(() => {
        lyricScrollTimerRef.current = null;
        const r = refsRef.current;
        if (!r?.showLyricsRef?.current) return;
        if (r?.currentLyricLineRef?.current !== targetIdx) return;
        const el = r?.lyricLineRefs?.current?.[targetIdx];
        if (el) scrollLyricToCenter(el);
      }, 260);
    }
  }, [refsRef, scrollLyricToCenter, updateMiniLyric]);

  return { applyLyricHighlight, clearLyricHighlight, updateMiniLyric };
}
